<template>
  <div class="flex flex-col h-full">
    <!-- Weekday header row -->
    <div
      class="grid flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]"
      :class="showWorkWeekOnly ? 'grid-cols-5' : 'grid-cols-7'"
    >
      <div
        v-for="d in dayHeaders"
        :key="d"
        class="py-2 text-center text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide"
      >
        {{ d }}
      </div>
    </div>

    <!-- Month grid -->
    <div class="flex-1 overflow-y-auto">
      <div
        class="grid h-full"
        :class="showWorkWeekOnly ? 'grid-cols-5' : 'grid-cols-7'"
        :style="{ gridTemplateRows: `repeat(${weeks.length}, minmax(100px, 1fr))` }"
      >
        <div
          v-for="day in allDays"
          :key="day.key"
          class="border-b border-r border-gray-100 dark:border-gray-800 p-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
          :class="!day.currentMonth ? 'bg-gray-50/50 dark:bg-[#0d1117]/60' : ''"
          @click="emit('day-click', day.date)"
        >
          <!-- Day number -->
          <div class="flex justify-center mb-1">
            <span
              class="w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold"
              :class="
                day.isToday
                  ? 'bg-blue-600 text-white'
                  : day.currentMonth
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-300 dark:text-gray-600'
              "
            >{{ day.dayNum }}</span>
          </div>

          <!-- Events (max 3 visible) -->
          <div class="space-y-0.5">
            <div
              v-for="booking in visibleBookings(day.date)"
              :key="booking.id"
              class="flex items-center gap-1 rounded px-1 py-0.5 cursor-pointer hover:opacity-80 transition-opacity"
              :style="chipStyle(booking)"
              @click.stop="emit('event-click', booking, $event.currentTarget.getBoundingClientRect())"
            >
              <svg v-if="booking.internal_notes" class="w-2.5 h-2.5 flex-shrink-0" :style="chipTextStyle(booking)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Has private notes">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <span class="text-[10px] font-medium truncate" :style="chipTextStyle(booking)">
                {{ booking.title || booking.name }}
              </span>
            </div>

            <div
              v-if="overflowCount(day.date) > 0"
              class="text-[10px] text-gray-400 dark:text-gray-500 px-1 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300"
              @click.stop="emit('day-click', day.date)"
            >
              +{{ overflowCount(day.date) }} more
            </div>

            <!-- Tasks (Google-Calendar-Tasks style overlay) -->
            <CalendarTaskChip
              v-for="task in visibleTasks(day.date)"
              :key="task.id"
              :task="task"
              @open="openTask"
              @toggle="toggleTask"
            />
            <div
              v-if="taskOverflowCount(day.date) > 0"
              class="text-[10px] text-gray-400 dark:text-gray-500 px-1"
            >
              +{{ taskOverflowCount(day.date) }} more tasks
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCalendarStore }         from 'stores/calendar.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'
import { useTasksStore }            from 'stores/tasks.js'
import { useTaskDates }             from 'composables/useTaskDates.js'
import CalendarTaskChip             from './CalendarTaskChip.vue'

const MAX_VISIBLE = 3

const props = defineProps({
  selectedDate: { type: Date, required: true },
})

const emit = defineEmits(['event-click', 'day-click'])

// Solid booking color; hatched like Google Calendar when the owner's RSVP is "maybe"
function darkenColor(hex, factor = 0.3) {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  const n = parseInt(full, 16)
  const r = Math.round(((n >> 16) & 0xff) * (1 - factor))
  const g = Math.round(((n >> 8)  & 0xff) * (1 - factor))
  const b = Math.round(( n        & 0xff) * (1 - factor))
  return `rgb(${r},${g},${b})`
}

function chipStyle(booking) {
  const color = booking.backgroundColor || booking.color_css_code || '#3b82f6'
  if (booking.calendar_owner_status === 'declined') {
    return { backgroundColor: 'white', border: `1px solid ${color}` }
  }
  if (booking.calendar_owner_status === 'tentative') {
    const dark = darkenColor(color)
    return { backgroundImage: `repeating-linear-gradient(45deg, ${color} 0px, ${color} 8px, ${dark} 8px, ${dark} 10px)` }
  }
  return { backgroundColor: color }
}

function chipTextStyle(booking) {
  const color = booking.backgroundColor || booking.color_css_code || '#3b82f6'
  if (booking.calendar_owner_status === 'declined') {
    return { color, textDecoration: 'line-through' }
  }
  return { color: '#ffffff' }
}

const store         = useCalendarStore()
const settingsStore = useCalendarSettingsStore()

const today = new Date(); today.setHours(0, 0, 0, 0)

const showWorkWeekOnly = computed(() => settingsStore.show_only_work_week)
const cols             = computed(() => showWorkWeekOnly.value ? 5 : 7)

const dayHeaders = computed(() =>
  showWorkWeekOnly.value
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
)

// ── Calendar grid computation ─────────────────────────────────────────────────

const allDays = computed(() => {
  const year     = props.selectedDate.getFullYear()
  const month    = props.selectedDate.getMonth()
  const first    = new Date(year, month, 1)
  const last     = new Date(year, month + 1, 0)
  const workWeek = showWorkWeekOnly.value
  const isWeekend = d => d.getDay() === 0 || d.getDay() === 6

  // In work-week mode use Monday (1) as column 0, otherwise Sunday (0)
  const startDow = workWeek ? 1 : 0
  const offset   = (first.getDay() - startDow + 7) % 7

  const days = []

  // Leading days from previous month
  for (let i = 0; i < offset; i++) {
    const d = new Date(year, month, 1 - offset + i); d.setHours(0,0,0,0)
    if (workWeek && isWeekend(d)) continue
    days.push({ date: d, dayNum: d.getDate(), currentMonth: false, isToday: false, key: `p${i}` })
  }

  // Current month
  for (let i = 1; i <= last.getDate(); i++) {
    const d = new Date(year, month, i); d.setHours(0,0,0,0)
    if (workWeek && isWeekend(d)) continue
    days.push({ date: d, dayNum: i, currentMonth: true, isToday: d.getTime() === today.getTime(), key: `c${i}` })
  }

  // Trailing days to complete final row
  let nextDay = 1
  while (days.length % cols.value !== 0) {
    const d = new Date(year, month + 1, nextDay++); d.setHours(0,0,0,0)
    if (workWeek && isWeekend(d)) continue
    days.push({ date: d, dayNum: d.getDate(), currentMonth: false, isToday: false, key: `n${nextDay}` })
  }

  return days
})

const weeks = computed(() => {
  const rowCount = Math.ceil(allDays.value.length / cols.value)
  return Array.from({ length: rowCount }, (_, i) => i)
})

// ── Bookings per day ──────────────────────────────────────────────────────────

function bookingsForDay(date) {
  const dayEnd = new Date(date); dayEnd.setHours(23, 59, 59, 999)
  return store.filteredBookings.filter(b => {
    const start = new Date(b.start_at ?? b.start)
    return start >= date && start <= dayEnd
  })
}

function visibleBookings(date) {
  return bookingsForDay(date).slice(0, MAX_VISIBLE)
}

function overflowCount(date) {
  return Math.max(0, bookingsForDay(date).length - MAX_VISIBLE)
}

// ── Tasks per day ────────────────────────────────────────────────────────────

const MAX_VISIBLE_TASKS = 2
const tasksStore = useTasksStore()
const router = useRouter()
const { dateStrInUserTz } = useTaskDates()

function cellDateStr(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function tasksForDay(date) {
  const key = cellDateStr(date)
  return store.filteredTasks.filter(t => dateStrInUserTz(t.due_at) === key)
}

function visibleTasks(date) {
  return tasksForDay(date).slice(0, MAX_VISIBLE_TASKS)
}

function taskOverflowCount(date) {
  return Math.max(0, tasksForDay(date).length - MAX_VISIBLE_TASKS)
}

function openTask(task) {
  router.push(`/tasks/${task.id}`)
}

async function toggleTask(task) {
  const next = task.status === 'completed' ? 'pending' : 'completed'
  const updated = await tasksStore.setStatus(task, next)
  if (updated) store.updateTask(updated)
}

// ── Data fetching ─────────────────────────────────────────────────────────────

async function fetchMonth(date) {
  const year  = date.getFullYear()
  const month = date.getMonth()
  const start = new Date(year, month, 1).toISOString().split('T')[0]
  const end   = new Date(year, month + 1, 0).toISOString().split('T')[0]
  await Promise.all([store.fetchBookings(start, end), store.fetchTasks(start, end)])
}

onMounted(() => fetchMonth(props.selectedDate))
watch(() => props.selectedDate, fetchMonth)
</script>
